import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Bag } from '../../../assests/ExportImageIcon'
import { showToast } from '../../../componants/toast/toastService'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import '../venueModals.css'
import './venueNegotiation.css'

const WS_RECONNECT_DELAY_MS = 3000
const WS_MAX_RECONNECT_ATTEMPTS = 5

const AttachIcon = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <path d="M13.5 7.5 8.2 12.8a2.5 2.5 0 0 0 3.54 3.54l5.65-5.65a4 4 0 1 0-5.66-5.66l-6.36 6.37" stroke="#6B7280" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const SendIcon = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <path d="M2.5 17.5 17.5 10 2.5 2.5l2.5 6.5-2.5 1 2.5 1 -2.5 6.5Z" fill="#026D4D" />
    </svg>
)

const formatTime = (dateStr) => {
    if (!dateStr) return ''
    // The REST API sometimes returns "YYYY-MM-DD HH:mm:ss" (space-separated,
    // no timezone) instead of strict ISO — normalize to "T" so this parses
    // reliably across browsers (Safari doesn't accept the space variant).
    const normalized = typeof dateStr === 'string' ? dateStr.replace(' ', 'T') : dateStr
    const date = new Date(normalized)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })
}

const formatMoney = (value, currency) => {
    if (value === undefined || value === null || value === '') return '—'
    const amount = Number(value)
    if (Number.isNaN(amount)) return String(value)
    const symbol = !currency || currency === 'INR' ? '₹' : `${currency} `
    return `${symbol}${amount.toLocaleString('en-IN')}`
}

// The nested `quotation` object (only present when is_quotation is true)
// carries the full fare breakdown for the offer card. `status` drives
// whether this offer still shows Accept/Decline or a final status note —
// same accepted/rejected vocabulary as the rest of the venue flow (see
// venueBookingConfirm.jsx's STATUS_LABELS).
const mapQuotationSummary = (quotation) => {
    if (!quotation) return null
    return {
        id: quotation.id,
        status: String(quotation.status || 'pending').toLowerCase(),
        fareRows: Array.isArray(quotation.fare_breakups)
            ? quotation.fare_breakups.map((item) => ({ id: item.id, label: item.name || 'Item', value: formatMoney(item.amount, item.currency) }))
            : [],
        totalAmount: formatMoney(quotation.total_amount, quotation.currency),
    }
}

// REST shape: `message` is an object {text, attachment}.
const mapMessage = (raw) => {
    const messageField = raw.message
    const isMessageObject = messageField !== null && typeof messageField === 'object'
    return {
        id: raw.id,
        text: (isMessageObject ? messageField.text : messageField) || '',
        attachment: (isMessageObject ? messageField.attachment : raw.attachment) || null,
        isQuotation: !!raw.is_quotation,
        quotation: mapQuotationSummary(raw.quotation),
        senderName: raw.sender_name || '',
        isVendor: raw.is_vendor === true || String(raw.is_vendor).toLowerCase() === 'true',
        time: formatTime(raw.created_at),
        createdAt: raw.created_at,
    }
}

// WebSocket `chat_message` push shape: flat, `message` is plain text,
// `message_id` instead of `id`, `attachment_url` instead of nested
// attachment, and no `quotation` object (just the `is_quotation` flag) — a
// different shape from both REST list items and REST send responses.
const mapWsChatMessage = (payload) => ({
    id: payload.message_id,
    text: payload.message || '',
    attachment: payload.attachment_url || null,
    isQuotation: !!payload.is_quotation,
    quotation: null,
    senderName: payload.sender_name || '',
    isVendor: payload.is_vendor === true,
    time: formatTime(payload.created_at),
    createdAt: payload.created_at,
})

const wsBaseUrl = () => {
    try {
        const url = new URL(process.env.REACT_APP_API_BASE_URL || '')
        return `${url.protocol === 'https:' ? 'wss:' : 'ws:'}//${url.host}`
    } catch {
        return ''
    }
}

const VenueNegotiation = () => {
    // `id` (route param) is the quotation/proposal id — unchanged, still
    // used for proposalAction (accept/decline a quotation from the chat)
    // and the confirm-booking navigation on accept.
    const { id } = useParams()
    const { state: routerState } = useLocation()
    // Corrected 2026-09-21 per Jatin: the negotiation messages endpoint (and
    // presumably the chat socket, same underlying room) is now keyed by
    // chat_room_id — a DIFFERENT id from the quotation/proposal id above.
    // Passed via nav state from whichever page opened this chat (proposal
    // detail's own chat_room_id, or a pre-fetch from the quotations list).
    const chatRoomId = routerState?.chatRoomId ?? null
    const navigate = useNavigate()
    const accessToken = useSelector((state) => state.auth?.access_token)

    const [messages, setMessages] = useState([])
    const [loading, setLoading] = useState(true)
    const [draft, setDraft] = useState('')
    const [attachment, setAttachment] = useState(null)
    const [sending, setSending] = useState(false)
    const [wsConnected, setWsConnected] = useState(false)
    const [quotationBusyId, setQuotationBusyId] = useState(null)

    const fileInputRef = useRef(null)
    const bodyRef = useRef(null)
    const wsRef = useRef(null)
    const reconnectAttemptsRef = useRef(0)
    const reconnectTimerRef = useRef(null)
    const unmountedRef = useRef(false)

    const mergeMessage = (mapped) => {
        setMessages((prev) => (prev.some((m) => m.id === mapped.id) ? prev : [...prev, mapped]))
    }

    // REST — initial history load + fallback if the socket never connects.
    const fetchMessages = () => {
        if (!chatRoomId) return
        service
            .get(endpoints.venues.negotiationMessages.replace('{chat_room_id}', chatRoomId), { page: 1, page_size: 100 }, { silent: true })
            .then(({ data }) => {
                const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []
                setMessages(list.map(mapMessage).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)))
            })
            .catch(() => setMessages([]))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        fetchMessages()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatRoomId])

    // WebSocket — live text delivery. History/files/quotation-actions stay REST.
    useEffect(() => {
        if (!chatRoomId || !accessToken) return undefined
        unmountedRef.current = false

        const connect = () => {
            const base = wsBaseUrl()
            if (!base) return
            const ws = new WebSocket(`${base}/ws/user/negotiations/${chatRoomId}/chat/?token=${encodeURIComponent(accessToken)}`)
            wsRef.current = ws

            ws.onopen = () => {
                reconnectAttemptsRef.current = 0
                setWsConnected(true)
                // Inferred, not independently confirmed: the join payload key
                // switched to match the same chat_room_id the REST/WS URL
                // now use — verify against a real socket if messages don't
                // start flowing.
                ws.send(JSON.stringify({ chat_room_id: Number(chatRoomId) }))
            }

            ws.onmessage = (event) => {
                let payload
                try {
                    payload = JSON.parse(event.data)
                } catch {
                    return
                }
                if (payload.type === 'chat_history') {
                    const list = Array.isArray(payload.messages) ? payload.messages : []
                    setMessages(list.map(mapMessage).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)))
                } else if (payload.type === 'chat_message') {
                    if (payload.is_quotation) {
                        // The live push doesn't carry the full quotation
                        // breakdown — refetch over REST to get it rather
                        // than show an incomplete offer card.
                        fetchMessages()
                    } else {
                        mergeMessage(mapWsChatMessage(payload))
                    }
                }
            }

            ws.onclose = () => {
                setWsConnected(false)
                if (unmountedRef.current) return
                if (reconnectAttemptsRef.current >= WS_MAX_RECONNECT_ATTEMPTS) return
                reconnectAttemptsRef.current += 1
                reconnectTimerRef.current = setTimeout(connect, WS_RECONNECT_DELAY_MS)
            }

            ws.onerror = () => {
                ws.close()
            }
        }

        connect()

        return () => {
            unmountedRef.current = true
            clearTimeout(reconnectTimerRef.current)
            wsRef.current?.close()
            wsRef.current = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatRoomId, accessToken])

    useEffect(() => {
        bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight })
    }, [messages])

    const vendorName = messages.find((m) => m.isVendor)?.senderName || 'Vendor'

    const sendMessage = () => {
        const text = draft.trim()
        if ((!text && !attachment) || sending || !chatRoomId) return
        setSending(true)

        // Text-only goes over the socket (no local echo — the UI waits for
        // the chat_message event to append it). Any attachment always goes
        // over REST multipart, per spec, and its response is appended
        // directly since sockets here only carry text.
        if (!attachment && wsConnected && wsRef.current) {
            wsRef.current.send(JSON.stringify({ message: text }))
            setDraft('')
            setSending(false)
            return
        }

        const payload = new FormData()
        payload.append('message', text)
        if (attachment) payload.append('attachment', attachment)

        service
            .post(endpoints.venues.negotiationMessages.replace('{chat_room_id}', chatRoomId), payload)
            .then(({ data }) => {
                const created = data?.data ?? data
                if (created?.id) {
                    mergeMessage(mapMessage(created))
                } else {
                    fetchMessages()
                }
                setDraft('')
                setAttachment(null)
                if (fileInputRef.current) fileInputRef.current.value = ''
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setSending(false))
    }

    const handleAttachmentChange = (e) => {
        const file = e.target.files?.[0] || null
        setAttachment(file)
        if (file) showToast(`${file.name} attached — will send with your next message`, 'success')
    }

    // 2026-09-21 per Jatin: Accept doesn't call `action` for now (that
    // wiring is coming later, per-page, on his instruction) — it calls
    // confirm-booking-details instead, same GET already used to load
    // venueConfirmBooking.jsx itself. Decline is unchanged (still `action`).
    const handleQuotationAction = (action, quotationId) => {
        if (!id || quotationBusyId) return
        setQuotationBusyId(quotationId)

        const request = action === 'accept'
            ? service.get(endpoints.venues.proposalConfirmBookingDetails.replace('{proposal_id}', id), {}, { silent: true })
            : service.post(endpoints.venues.proposalAction.replace('{proposal_id}', id), {
                action,
                reason: 'Declined',
                quotation_id: quotationId,
            })

        request
            .then(() => {
                if (action === 'accept') {
                    navigate(`/venue-confirm-booking/${id}`)
                } else {
                    // service.js's response interceptor already shows the
                    // server's own message ("You have rejected the
                    // quotation.") — this was a redundant second toast.
                    fetchMessages()
                }
            })
            .catch(() => {
                // Decline's error toast comes from the interceptor (not
                // silent); Accept is silent, so show one manually here.
                if (action === 'accept') showToast('Something went wrong. Please try again.', 'error')
            })
            .finally(() => setQuotationBusyId(null))
    }

    return (
        <section className='commonTopPadding commonBottomPadding'>
            <div className='container'>
                <div className='venueChat bgWhite commonBorder borderRadius20'>
                    <div className='venueChat__head'>
                        <div className='hotelCard__name'>{vendorName}</div>
                    </div>

                    <div className='venueChat__body' ref={bodyRef}>
                        {loading ? (
                            <p className='hotelResultsEmpty'>Loading conversation...</p>
                        ) : messages.length === 0 ? (
                            <p className='hotelResultsEmpty'>No messages yet — say hello to get the conversation started.</p>
                        ) : (
                            messages.map((message) => (
                                message.isQuotation ? (
                                    <div className='venueChatOffer' key={message.id}>
                                        <div className='venueChatOffer__head'>
                                            <span className='venueChatOffer__badge'>
                                                <img src={Bag} alt="" />
                                            </span>
                                            <span className='venueChatOffer__title'>Quotation Update</span>
                                            <span className='venueChatOffer__time'>{message.time}</span>
                                        </div>
                                        {message.text ? <p className='hotelCard__locationNote'>{message.text}</p> : null}
                                        {message.quotation ? (
                                            <>
                                                {message.quotation.fareRows.map((row) => (
                                                    <div className='bsFareRow' key={row.id}>
                                                        <span>{row.label}</span>
                                                        <span>{row.value}</span>
                                                    </div>
                                                ))}
                                                <div className='bsFareDivider' />
                                                <div className='bsFareRow bsFareRow--total'>
                                                    <span>TOTAL AMOUNT</span>
                                                    <span>{message.quotation.totalAmount}</span>
                                                </div>
                                                {message.quotation.status === 'accepted' ? (
                                                    <p className='venueChatOffer__statusNote'>You have successfully accepted the quotation!</p>
                                                ) : message.quotation.status === 'rejected' || message.quotation.status === 'declined' ? (
                                                    <p className='venueChatOffer__statusNote'>You have rejected the quotation.</p>
                                                ) : (
                                                    <div className='venueChatOffer__actions'>
                                                        <button
                                                            type='button'
                                                            className='venueModalBtn venueModalBtn--danger venueChatOffer__btn'
                                                            disabled={quotationBusyId === message.quotation.id}
                                                            onClick={() => handleQuotationAction('reject', message.quotation.id)}
                                                        >
                                                            Decline
                                                        </button>
                                                        <button
                                                            type='button'
                                                            className='commonBtn venueChatOffer__btn'
                                                            style={{ margin: 0 }}
                                                            disabled={quotationBusyId === message.quotation.id}
                                                            onClick={() => handleQuotationAction('accept', message.quotation.id)}
                                                        >
                                                            Accept
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        ) : null}
                                    </div>
                                ) : (
                                    <div className={`venueChatBubble venueChatBubble--${message.isVendor ? 'vendor' : 'me'}`} key={message.id}>
                                        <p>{message.text}</p>
                                        {message.attachment ? (
                                            <a className='venueChatBubble__attachment' href={message.attachment} target='_blank' rel='noreferrer'>
                                                View Attachment
                                            </a>
                                        ) : null}
                                        <span className='venueChatBubble__time'>{message.time}</span>
                                    </div>
                                )
                            ))
                        )}
                    </div>

                    <div className='venueChat__inputRow'>
                        <input
                            ref={fileInputRef}
                            type='file'
                            hidden
                            onChange={handleAttachmentChange}
                        />
                        <span
                            className={`venueChat__attachBtn ${attachment ? 'is-active' : ''}`}
                            onClick={() => fileInputRef.current?.click()}
                            role="button"
                            tabIndex={0}
                            aria-label="Attach file"
                        >
                            <AttachIcon />
                        </span>
                        <input
                            type='text'
                            placeholder='Type your message...'
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                        />
                        <button type='button' className='venueChat__sendBtn' onClick={sendMessage} disabled={sending} aria-label="Send message">
                            <SendIcon />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default VenueNegotiation
